/**
 * SaveAsTestButton — the P8 "save the current stepping run as a test" action.
 *
 * Reads the scrubber's live answers (walker state) plus the active process +
 * resolved model, and on click captures them into a `test` block appended to the
 * process's file (via {@link captureAndSaveTest}). A transient status line
 * reports the write result or an under-constrained-session warning. Thin glue —
 * the capture/round-trip logic lives in `captureTest.ts` and is unit-tested.
 */
import { useCallback, useState, type ReactElement } from "react";
import type { ProcessModel, ResolvedModel } from "@archlang/engine";
import { Button } from "@archlang/ui";
import type { WalkerState } from "../hooks/useProcessPlayback.js";
import { useModelMutations } from "../workbench/mutations.js";
import { captureAndSaveTest } from "./captureTest.js";

export interface SaveAsTestButtonProps {
  readonly process: ProcessModel;
  readonly processId: string;
  readonly model: ResolvedModel;
  readonly state: WalkerState;
}

export function SaveAsTestButton({ process, processId, model, state }: SaveAsTestButtonProps): ReactElement {
  const mutations = useModelMutations();
  const [status, setStatus] = useState<string | null>(null);

  const onSave = useCallback(() => {
    const name = window.prompt("Name this test:", `${process.name} run`)?.trim();
    if (!name) return;
    const { mutation, diagnostics } = captureAndSaveTest({ process, processId, model, state, name, mutations });
    if (mutation.status !== "applied") {
      setStatus(mutation.status === "stale" ? "Recompiling — try again." : `Couldn't save (${mutation.status}).`);
      return;
    }
    const warn = diagnostics.find((d) => d.severity !== "error");
    setStatus(warn ? "Saved (session under-constrained — review the pins)." : "Saved as test.");
  }, [process, processId, model, state, mutations]);

  return (
    <div className="flex flex-col gap-1">
      <Button variant="secondary" size="sm" onClick={onSave} data-testid="save-as-test">
        Save as test
      </Button>
      {status ? <span className="text-[11px] text-slate-400">{status}</span> : null}
    </div>
  );
}
